import React, {useState, useEffect} from 'react'
import * as Yup from 'yup'
import {useFormik} from 'formik'
import {useNavigate} from 'react-router-dom'
import axios from 'axios'
import {API_URL, SETUP_API} from '../../../config/api.config'
import clsx from 'clsx'
import {UpdateConfirmationDialog} from './UpdateConfirmationDialog'
import {UpdateProgressDialog} from './UpdateProgressDialog'
import {NetworkStatusIndicator} from '../../../pages/wifi-settings/NetworkStatusIndicator'
import {WifiSettingsModal} from '../../../pages/wifi-settings/WifiSettingsModal'

const orgIdSchema = Yup.object().shape({
  orgId: Yup.string()
    .min(3, 'Minimum 3 characters')
    .max(50, 'Maximum 50 characters')
    .required('Organization ID is required'),
})

const initialValues = {
  orgId: '',
}

export function OrganizationSetup() {
  const [loading, setLoading] = useState(false)
  const [setupStatus, setSetupStatus] = useState<null | {success: boolean; message: string}>(null)
  const [updateAvailable, setUpdateAvailable] = useState(false)
  const [currentVersion, setCurrentVersion] = useState('')
  const [latestVersion, setLatestVersion] = useState('')
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [showUpdateDialog, setShowUpdateDialog] = useState(false)
  const [showWifiModal, setShowWifiModal] = useState(false)
  const [hideWifi, setHideWifi] = useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    axios.get(`${API_URL}/api/open/version-check`)
      .then(({data}) => {
        setCurrentVersion(data.currentVersion || '')
        setLatestVersion(data.latestVersion || '')
        setUpdateAvailable(data.updateAvailable === true)
      })
      .catch(() => {})
  }, [])

  useEffect(() => {
    axios.get(`${API_URL}/api/open/login-config`)
      .then(({data}) => {
        setHideWifi(data.hideWifiOnLoginScreen === true)
      })
      .catch(() => {})
  }, [])

  const formik = useFormik({
    initialValues,
    validationSchema: orgIdSchema,
    onSubmit: async (values, {setStatus, setSubmitting}) => {
      setLoading(true)
      try {
        const {data} = await axios.post(SETUP_API.SETUP_ORGANIZATION, {
          orgId: values.orgId,
        })

        setSetupStatus({
          success: data.success,
          message: data.message,
        })

        if (data.success) {
          // User will click Login manually
        }
      } catch (error) {
        console.error('Setup error:', error)
        setStatus('Error setting up organization. Please try again.')
        setSetupStatus({
          success: false,
          message: 'Error setting up organization. Please check your Organization ID or try again later.',
        })
      } finally {
        setSubmitting(false)
        setLoading(false)
      }
    },
  })

  return (
    <form
      className='form w-100'
      onSubmit={formik.handleSubmit}
      noValidate
      id='kt_organization_setup_form'
    >
      <div className='text-center mb-11'>
        <h1 className='text-dark fw-bolder mb-3'>Organization Setup</h1>
        <div className='text-gray-500 fw-semibold fs-6'>Enter your Organization ID to set up this client</div>
      </div>

      {!hideWifi && <NetworkStatusIndicator onConfigureClick={() => setShowWifiModal(true)} />}

      {setupStatus?.success ? (
        <div className='mb-10 bg-light-success p-8 rounded-3 text-center'>
          <div className='symbol symbol-60px mb-4'>
            <div className='symbol-label bg-success'>
              <i className='ki-duotone ki-check fs-2x text-white'>
                <span className='path1'></span>
                <span className='path2'></span>
              </i>
            </div>
          </div>
          <h3 className='text-success fw-bold mb-2'>Welcome to RevCon Outpost!</h3>
          <div className='text-gray-600 fs-7'>
            {setupStatus.message}. Your client is now connected and ready to use.
          </div>
          <div className='text-gray-500 fs-8 mt-2'>Click <strong>Login</strong> below to get started.</div>
        </div>
      ) : setupStatus && !setupStatus.success ? (
        <div className='mb-10 bg-light-danger p-8 rounded'>
          <div className='text-danger'>{setupStatus.message}</div>
        </div>
      ) : (
        <div className='mb-10 bg-light-info p-8 rounded'>
          <div className='text-info'>
            No users found. Please enter your <strong>Organization ID</strong> to connect to the server
            and download user data.
          </div>
        </div>
      )}

      <div className='fv-row mb-8'>
        <label className='form-label fs-6 fw-bolder text-dark'>Organization ID</label>
        <input
          placeholder='Enter your organization ID'
          {...formik.getFieldProps('orgId')}
          className={clsx(
            'form-control bg-transparent',
            {'is-invalid': formik.touched.orgId && formik.errors.orgId},
            {'is-valid': formik.touched.orgId && !formik.errors.orgId}
          )}
          type='text'
          name='orgId'
          autoComplete='off'
          disabled={setupStatus?.success}
        />
        {formik.touched.orgId && formik.errors.orgId && (
          <div className='fv-plugins-message-container'>
            <span role='alert'>{formik.errors.orgId}</span>
          </div>
        )}
      </div>

      <div className='d-flex mb-10 gap-3'>
        {setupStatus?.success ? (
          <a href='/auth' className='btn btn-primary flex-fill'>
            Login
          </a>
        ) : (
          <button
            type='submit'
            id='kt_setup_submit'
            className='btn btn-primary flex-fill'
            disabled={formik.isSubmitting || !formik.isValid || loading}
          >
            {!loading && <span className='indicator-label'>Setup Organization</span>}
            {loading && (
              <span className='indicator-progress' style={{display: 'block'}}>
                Please wait...
                <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
              </span>
            )}
          </button>
        )}
      </div>

      {updateAvailable && (
        <div className='mb-8 bg-light-warning p-5 rounded-3 border border-warning border-dashed'>
          <div className='d-flex align-items-center'>
            <div className='symbol symbol-40px me-3'>
              <div className='symbol-label bg-warning'>
                <i className='ki-duotone ki-arrow-up fs-2 text-white'>
                  <span className='path1'></span>
                  <span className='path2'></span>
                </i>
              </div>
            </div>
            <div className='flex-grow-1'>
              <div className='text-warning fw-bold fs-6 mb-1'>Update Available</div>
              <div className='text-gray-700 fs-7'>
                A new version <strong>{latestVersion}</strong> is available.
                You are currently on <strong>{currentVersion}</strong>.
              </div>
            </div>
          </div>
          <button
            type='button'
            className='btn btn-sm btn-warning mt-3 w-100'
            onClick={() => setShowConfirmDialog(true)}
          >
            <i className='ki-duotone ki-arrow-up fs-4 me-1'>
              <span className='path1'></span>
              <span className='path2'></span>
            </i>
            Update Now
          </button>
        </div>
      )}

      <UpdateConfirmationDialog
        show={showConfirmDialog}
        currentVersion={currentVersion}
        latestVersion={latestVersion}
        onCancel={() => setShowConfirmDialog(false)}
        onConfirm={() => {
          setShowConfirmDialog(false)
          setShowUpdateDialog(true)
        }}
      />

      <UpdateProgressDialog
        show={showUpdateDialog}
        onComplete={() => setShowUpdateDialog(false)}
      />

      {!hideWifi && (
        <WifiSettingsModal
          show={showWifiModal}
          onHide={() => setShowWifiModal(false)}
        />
      )}
    </form>
  )
}
